// Who looks after which customers (Admin → Customer assignments): each person starts the Worklist
// and History on their customers — Owner and Backup alike — unless a saved default or the address
// says otherwise (`view-url.ts` `openingViewOf`). A label as the team's sheet says it ("Macys/
// Bloomingdale's") covers customers by key, `<source>:<code>` (A2000:ROSSSTORES, Deposco:…), so an
// A2000 code and a Deposco code alike never mix. A label with no key yet is "Not linked yet": a manager
// links it, or retires it; a match by name is only ever suggested, never saved by itself.

import type { CustomerRef, LineView } from "./assemble";
import { defaultShown } from "./customer-names";
import { customerSpelling } from "./excel";
import type { LineSource } from "./deposco";
import { LINE_SOURCES, deposcoKey } from "./deposco";
import type { AppUserRecord, CustomerAssignmentRecord, SourceRow, UserPreferenceRecord } from "./types";

/** An email as the app compares it: sign-in may give Maria.Elma.Burleigh@… or maria.elma.burleigh@…. */
export const emailKey = (v: unknown): string =>
  String(v ?? "")
    .trim()
    .toLowerCase();

/** An email as a person's record needs it: something@something, no spaces. */
export const isEmail = (v: unknown): boolean => /^[^\s@]+@[^\s@]+$/.test(String(v ?? "").trim());

/** A customer's key: its source and its code, the code as the source has it (trimmed, upper-case). */
export const customerKey = (source: LineSource, code: string): string => `${source}:${code.trim().toUpperCase()}`;
export const keyOfCustomer = (c: Pick<CustomerRef, "source" | "code">) => customerKey(c.source, c.code);
/** A key's source, read from the key itself (a linked code no list has now still shows it); undefined if none. */
export const sourceOfCustomerKey = (key: string): LineSource | undefined =>
  LINE_SOURCES.find((s) => key.startsWith(`${s}:`));

/** A record's keys: "; " between them, each once, blanks left out. */
export const splitKeys = (text: string | null | undefined): string[] => [
  ...new Set(
    String(text ?? "")
      .split(";")
      .map((k) => k.trim())
      .filter(Boolean),
  ),
];
export const joinKeys = (keys: readonly string[]): string => [...new Set(keys)].join("; ");

/** An assignment that counts: not retired. */
const active = (a: CustomerAssignmentRecord) => a.retired !== "Yes";

/**
 * A person added with no customer yet (Add with a person only): a record with no label and no linked
 * code. It covers nothing — My customers, an Owner, the counts all go by linked codes — and only puts
 * the person on the page, "0 customers", until they get one: the first assignment added for them fills
 * this record (`personOnlyRow`) rather than adding another.
 */
export const isPersonOnly = (a: Pick<CustomerAssignmentRecord, "label" | "customerKeys">) =>
  !String(a.label ?? "").trim() && !splitKeys(a.customerKeys).length;

/** A person's own person-only record, not retired: what their first assignment fills. */
export const personOnlyRow = (assignments: readonly CustomerAssignmentRecord[], email: string) =>
  assignments.find((a) => active(a) && isPersonOnly(a) && emailKey(a.personEmail) === emailKey(email));

/** A record's keys with `keys` added (Link, Link all, an unlink's Undo), as it is now on the server. */
export const linkKeys = (text: string | null | undefined, keys: readonly string[]) =>
  joinKeys([...splitKeys(text), ...keys]);
/** A record's keys without `keys` (Unlink, Link all's Undo). */
export const unlinkKeys = (text: string | null | undefined, keys: readonly string[]) =>
  joinKeys(splitKeys(text).filter((k) => !keys.includes(k)));

/** A person's customers (Owner and Backup, not retired): their keys and labels. Unmatched labels add no key. */
export function myCustomers(
  assignments: readonly CustomerAssignmentRecord[],
  email: string,
): { keys: string[]; labels: string[] } {
  const me = emailKey(email);
  const mine = assignments.filter(
    (a) => active(a) && emailKey(a.personEmail) === me && splitKeys(a.customerKeys).length,
  );
  return {
    keys: [...new Set(mine.flatMap((a) => splitKeys(a.customerKeys)))].sort(),
    labels: [...new Set(mine.map((a) => a.label))].sort((a, b) => a.localeCompare(b)),
  };
}

/** "ROSS STORES, INC." → "ROSS STORES": how the workbook typed a company, without its form. */
const COMPANY_FORM = /[\s,.]+(inc|llc|corp|corporation|co|company|ltd)\.?$/i;

/**
 * The spellings (`customerSpelling`) a workbook row of my customers may have: it has a name only
 * ("Ross", "ROSS", "ROSSSTORES", "Ross Stores"). Each linked customer's code, its name with and
 * without its company form, and each part of its label ("Citi + Ross": citi, ross).
 */
export function mySpellings(
  keys: readonly string[],
  labels: readonly string[],
  nameOf: (key: string) => string | undefined,
): string[] {
  const out = new Set<string>();
  const add = (v: string | undefined) => {
    const s = customerSpelling(v);
    if (s.length >= 2) out.add(s);
  };
  for (const k of keys) {
    add(k.slice(k.indexOf(":") + 1));
    const name = nameOf(k);
    add(name);
    add(name?.replace(COMPANY_FORM, ""));
  }
  for (const l of labels) for (const part of l.split(/[/+,]/)) add(part);
  return [...out].sort();
}

/** My customers as a filter: their keys, and the spellings a workbook row (a name only) may have. */
export interface MyCustomerFilter {
  keys: readonly string[];
  spellings: readonly string[];
}

/** Whether a line holds one of my customers: by key, or a workbook row by its name's spelling. */
export const isMyLine = (l: Pick<LineView, "customers">, f: MyCustomerFilter): boolean =>
  l.customers.some((c) =>
    c.code ? f.keys.includes(keyOfCustomer(c)) : f.spellings.includes(customerSpelling(c.name)),
  );

/**
 * Whether a pre-route is one of my customers: by its order's customer code (an A2000 order, or a
 * Deposco SO), as a line by key; once the order is gone from the view, by the name kept on it.
 */
export const isMyPreRoute = (
  v: { record: { source: LineSource }; order: { customer_code: string | null } | null; customerName: string },
  f: MyCustomerFilter,
): boolean =>
  v.order?.customer_code
    ? f.keys.includes(customerKey(v.record.source, v.order.customer_code))
    : f.spellings.includes(customerSpelling(v.customerName));

/**
 * Whether a System log entry is one of my customers: its line's, as the Worklist; an entry with no
 * line (a list value, a division, a pickup, a pre-route) or a line not read stays shown.
 */
export const isMyChange = (line: Pick<LineView, "customers"> | null | undefined, f: MyCustomerFilter): boolean =>
  line ? isMyLine(line, f) : true;

/** The pages beyond the Worklist and History with My customers, and whether each opens with it on. */
export const MY_CUSTOMERS_PAGES = { shipping: true, "pre-route": true, "system-log": false } as const;
export type MyCustomersPage = keyof typeof MY_CUSTOMERS_PAGES;

/**
 * Whether such a page opens on My customers: the address's `v.my`, else this browser tab's last choice,
 * else the page's default (the System log off: people look at every change there). Never for someone
 * with no customers.
 */
export function openingMine(page: MyCustomersPage, hasMine: boolean, address: unknown, remembered: unknown): boolean {
  if (!hasMine) return false;
  for (const v of [address, remembered]) if (v === "1" || v === "0" || v === 1 || v === 0) return Number(v) === 1;
  return MY_CUSTOMERS_PAGES[page];
}

/** Every key some Owner has (not retired): a customer without one is unassigned. */
export const ownedKeys = (assignments: readonly CustomerAssignmentRecord[]): string[] =>
  [
    ...new Set(assignments.filter((a) => active(a) && a.role === "Owner").flatMap((a) => splitKeys(a.customerKeys))),
  ].sort();

/** A customer the data has: what the Admin tab links, and what "unassigned" counts. */
export interface CustomerOption {
  key: string;
  source: LineSource;
  code: string;
  name: string;
  /** Open Worklist lines holding it (a Mixed line counts under each); 0: "no open lines". */
  openLines: number;
  /** Active in A2000's customer master or Deposco's trading partners (`routing-customers`); one with open lines too. */
  active: boolean;
  /** Its shown name (Admin → Customers), when one is saved: said beside the original. */
  shown?: string;
}

/** A row of `routing-customers` (`dbo.vw_routing_customers`): every A2000 customer and Deposco trading partner. */
export interface CustomerMasterRow {
  source: LineSource;
  customer_code: string;
  customer_name: string;
  /** 1 active (A2000 ACTIVE = 'Y'), 0 not. */
  is_active: number;
  /** A2000's CUST_REF1 / CUST_REF2 as whole numbers (Deposco: null); absent while the view lacks them. */
  routing_open_bdays?: number | null;
  routing_due_bdays?: number | null;
}

/**
 * The customers to link: those of the open pick tickets and SOs, each once by key, the name the
 * source gives now, with their open lines (one per WMS #, Deposco customer + PO, or pick ticket
 * with no WMS #); and every customer of `master` (A2000's customer master, Deposco's trading
 * partners) with none — its name when the open lines give none.
 */
export function customerOptions(
  source: readonly SourceRow[],
  master: readonly CustomerMasterRow[] = [],
): CustomerOption[] {
  const found = new Map<string, { source: LineSource; code: string; name: string; lines: Set<string> }>();
  for (const s of source) {
    const src: LineSource = s.deposco ? "Deposco" : "A2000";
    const code = String(s.customer_code ?? "").trim();
    if (!code) continue;
    const key = customerKey(src, code);
    const line = s.deposco
      ? deposcoKey(s)
      : s.in_wms === 1 && s.wms_shipment
        ? `W|${s.wms_shipment}`
        : `P|${s.pick_ticket}`;
    const had = found.get(key) ?? { source: src, code, name: "", lines: new Set<string>() };
    had.name ||= String(s.customer_name ?? "").trim();
    had.lines.add(line);
    found.set(key, had);
  }
  const out = new Map<string, CustomerOption>(
    [...found].map(([key, c]) => [
      key,
      { key, source: c.source, code: c.code, name: c.name, openLines: c.lines.size, active: true },
    ]),
  );
  for (const m of master) {
    const code = String(m.customer_code ?? "").trim();
    if (!code) continue;
    const src: LineSource = m.source === "Deposco" ? "Deposco" : "A2000";
    const key = customerKey(src, code);
    const name = String(m.customer_name ?? "").trim();
    const active = Number(m.is_active) === 1;
    const had = out.get(key);
    if (had) out.set(key, { ...had, name: had.name || name });
    else out.set(key, { key, source: src, code, name, openLines: 0, active });
  }
  return [...out.values()].sort(
    (a, b) => (a.name || a.code).localeCompare(b.name || b.code) || a.key.localeCompare(b.key),
  );
}

/** A customer's name as the Admin lists say it: the original, its shown name first when one is saved. */
export const optionName = (c: Pick<CustomerOption, "code" | "name" | "shown">) =>
  c.shown && c.shown !== c.name && c.shown !== c.code ? `${c.shown} (${c.name || c.code})` : c.name;

/**
 * What the Link search offers: a customer with open lines, or an active one with none ("no open
 * lines"); an inactive one only when an assignment already links it (its chip keeps its name).
 */
export const customersToLink = (
  options: readonly CustomerOption[],
  assignments: readonly CustomerAssignmentRecord[],
): CustomerOption[] => {
  const linked = new Set(assignments.flatMap((a) => splitKeys(a.customerKeys)));
  return options.filter((c) => c.openLines > 0 || c.active || linked.has(c.key));
};

/** Whether the Link search lists it: a linked inactive customer is shown on its chip only. */
export const offeredToLink = (c: CustomerOption) => c.openLines > 0 || c.active;

/**
 * The My customers chip: "on" while it filters (× or All customers takes it off), "show" when the person
 * has customers and it is off — one click puts it back, the other filters as they are; none for someone
 * with no customers.
 */
export const myCustomersChip = (hasMine: boolean, mine: boolean): "on" | "show" | null =>
  !hasMine ? null : mine ? "on" : "show";

/** A customer searched for (the Link search, the customers with no owner): its source, code or name holds the words. */
export const customerMatches = (
  c: Pick<CustomerOption, "source" | "code" | "name" | "shown">,
  words: string,
): boolean => `${c.source} ${c.code} ${c.name} ${c.shown ?? ""}`.toLowerCase().includes(words.trim().toLowerCase());

/** The customers with open lines and no Owner: the managers' chip and the Admin tab's list. */
export const unassignedCustomers = (
  options: readonly CustomerOption[],
  assignments: readonly CustomerAssignmentRecord[],
): CustomerOption[] => {
  const owned = new Set(ownedKeys(assignments));
  return options.filter((c) => c.openLines > 0 && !owned.has(c.key));
};

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’`.]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * The customers a label may mean, by name or code — each part of "Macys/ Bloomingdale's" on its
 * own: a name or code that starts with it, or holds it as words. Only ever proposed: a manager
 * confirms each one (Ross the customer is not Ross the person). The keys already `linked` are left out,
 * so linking one keeps the others offered.
 */
export function proposedKeys(
  label: string,
  options: readonly CustomerOption[],
  linked: readonly string[] = [],
  max = 8,
): CustomerOption[] {
  return proposer(options)(label, linked, max);
}

/**
 * `proposedKeys` for many labels over the same customers: each customer's name and code normalised
 * once, not once per label (the tab's first render: every assignment over 2,000+ customers).
 */
export function proposer(options: readonly CustomerOption[]) {
  const prepared = options.map((c) => {
    const name = norm(c.name);
    return { c, name, spaced: ` ${name} `, tight: name.replace(/ /g, ""), code: norm(c.code).replace(/ /g, "") };
  });
  return (label: string, linked: readonly string[] = [], max = 8): CustomerOption[] => {
    const parts = label
      .replace(/\(new\)|-\s*new\b/gi, "")
      .split("/")
      .map(norm)
      .filter((p) => p.length >= 2)
      .map((p) => ({ p, spaced: ` ${p} `, tight: p.replace(/ /g, "") }));
    if (!parts.length) return [];
    const out: CustomerOption[] = [];
    for (const x of prepared) {
      if (out.length >= max) break;
      if (linked.includes(x.c.key)) continue;
      const hit = parts.some(
        (p) =>
          x.name.startsWith(p.p) ||
          x.spaced.includes(p.spaced) ||
          x.tight.startsWith(p.tight) ||
          x.code.startsWith(p.tight),
      );
      if (hit) out.push(x.c);
    }
    return out;
  };
}

// ---------------------------------------------------------------------------------------------
// Admin → Customer assignments: its three views (Needs confirming, Assignments, No owner)

/** "1 customer", "7 codes". */
const plural = (n: number, one: string) => `${n.toLocaleString("en-US")} ${n === 1 ? one : `${one}s`}`;

/** An assignment as the views read it: its linked keys, its suggested codes, and whether it needs confirming. */
export interface AssignmentView {
  row: CustomerAssignmentRecord;
  keys: string[];
  /** What its label may mean (`proposedKeys`), not linked yet: worked out on every read, never stored. Retired: none. */
  suggestions: CustomerOption[];
  /** Needs confirming ("Not linked yet"): active, a label with no linked code — suggestions waiting or none. */
  needs: boolean;
  /** A person with no customer yet (`isPersonOnly`): their header only, never a row. */
  person: boolean;
}

/** Every assignment's view; suggestions among the customers the Link search offers. */
export function assignmentViews(
  assignments: readonly CustomerAssignmentRecord[],
  customers: readonly CustomerOption[],
): AssignmentView[] {
  const propose = proposer(customers.filter(offeredToLink));
  return assignments.map((row) => {
    const keys = splitKeys(row.customerKeys);
    const person = isPersonOnly(row);
    const suggestions = active(row) && !person ? propose(row.label, keys) : [];
    return { row, keys, suggestions, needs: active(row) && !person && !keys.length, person };
  });
}

/** An assignment of the page's (not retired, not a person-only record): what Assignments counts. */
const counted = (v: AssignmentView) => active(v.row) && !v.person;

/** The tabs' counts: Needs confirming, Assignments (not retired), No owner (customers with open lines and no Owner). */
export function assignmentCounts(
  views: readonly AssignmentView[],
  customers: readonly CustomerOption[],
): { needs: number; assignments: number; noOwner: number } {
  return {
    needs: views.filter((v) => v.needs).length,
    assignments: views.filter(counted).length,
    noOwner: unassignedCustomers(
      customers,
      views.map((v) => v.row),
    ).length,
  };
}

/**
 * Whether Needs confirming lists a view: it needs confirming, or it was linked from there in this visit
 * (`linkedHere`, "Linked ✓ 1 code · Undo") — it keeps its place, so the next row's Link all never slides
 * under the pointer. Until the view changes or the page loads again.
 */
export const listedInNeeds = (v: AssignmentView, linkedHere: ReadonlyMap<string, readonly string[]>) =>
  v.needs || (linkedHere.has(v.row.id) && active(v.row));

/** A row's chips as unlinked in this visit left them: their order then, and the codes unlinked since. */
export interface HeldChips {
  order: readonly string[];
  unlinked: readonly string[];
}

/**
 * A row's linked chips, in every view: none moves under the pointer. A code unlinked in this visit stays
 * where it was, struck through ("AEROAG · unlinked · Undo"), until the view changes or the page loads;
 * codes linked since come after.
 */
export function keptChips(keys: readonly string[], held: HeldChips | undefined): { key: string; unlinked: boolean }[] {
  if (!held) return keys.map((key) => ({ key, unlinked: false }));
  const now = new Set(keys);
  const out = held.order.flatMap((key) =>
    now.has(key) ? [{ key, unlinked: false }] : held.unlinked.includes(key) ? [{ key, unlinked: true }] : [],
  );
  const placed = new Set(held.order);
  return [...out, ...keys.filter((k) => !placed.has(k)).map((key) => ({ key, unlinked: false }))];
}

/**
 * A row's chips split by where they show: those it had (`top`, in place, `keptChips`), and those linked in
 * this visit (`fresh`) — under "Linked ✓ 3 codes · Undo", which takes the "Suggested codes" line's place,
 * so nothing above moves when they are linked.
 */
export function chipsByPlace(
  keys: readonly string[],
  held: HeldChips | undefined,
  linkedHere: readonly string[] | undefined,
): { top: { key: string; unlinked: boolean }[]; fresh: { key: string; unlinked: boolean }[] } {
  const fresh = new Set(linkedHere ?? []);
  const all = keptChips(keys, held);
  return { top: all.filter((c) => !fresh.has(c.key)), fresh: all.filter((c) => fresh.has(c.key)) };
}

/** A code unlinked: the row's chips held as they were before it (the first unlink sets the order). */
export const holdUnlinked = (held: HeldChips | undefined, keys: readonly string[], key: string): HeldChips => ({
  order: held?.order ?? keys,
  unlinked: [...new Set([...(held?.unlinked ?? []), key])],
});

/**
 * No owner's label for a new assignment, prefilled: the first ticked item's shown name (a shown-name
 * group's name), in the list's order; "" with nothing ticked. Editable.
 */
export const noOwnerLabel = (items: readonly NoOwnerItem[], ticked: ReadonlySet<string>): string => {
  const first = items.find((i) => ticked.has(i.id));
  return first ? (first.shown ?? shownGroupName(first.codes[0]!)) : "";
};

/** The people on the page: those with an active row (a person-only one too), not those whose rows are all retired. */
export const activePeople = (assignments: readonly CustomerAssignmentRecord[]): number =>
  new Set(assignments.filter(active).map((a) => emailKey(a.personEmail))).size;

/** "Linked ✓ 1 code", "Linked ✓ 3 codes": a row linked from Needs confirming, kept in place. */
export const linkedHereText = (keys: readonly string[]) => `Linked ✓ ${plural(keys.length, "code")}`;

/** The unlink toast: "Unlinked CATCO from Catco (Elma)" — its Undo links it again. */
export const unlinkedText = (key: string, row: Pick<CustomerAssignmentRecord, "label" | "personName">) =>
  `Unlinked ${key.slice(key.indexOf(":") + 1)} from ${row.label} (${row.personName})`;

// Person groups (Needs confirming, Assignments): closed until opened; a search or a person chosen opens
// the groups it keeps, so a match is never behind a closed header.

/** The people a person opened in a view, as kept in this browser (JSON); anything else: none. */
export function openedFrom(raw: unknown): string[] {
  try {
    const v: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").map(emailKey) : [];
  } catch {
    return [];
  }
}

/**
 * Whether a person's group is open: while a search or a person filter is on, every group it keeps
 * (unless closed by hand since it started); else only those opened — every group starts closed.
 */
export const personGroupOpen = (
  email: string,
  s: { opened: ReadonlySet<string>; filtering: boolean; closedWhileFiltering: ReadonlySet<string> },
): boolean => (s.filtering ? !s.closedWhileFiltering.has(emailKey(email)) : s.opened.has(emailKey(email)));

export type AssignmentsView = "needs" | "assignments" | "no-owner";

/** The view the tab opens on: the person's last one (Needs confirming only while it has rows), else Needs confirming while it has rows, else Assignments. */
export function openingAssignmentsView(remembered: unknown, needs: number): AssignmentsView {
  if (remembered === "assignments" || remembered === "no-owner") return remembered;
  return needs > 0 ? "needs" : "assignments";
}

/** Someone else's assignment that links a code: shown beside it, never a rule. */
export interface CodeHolder {
  personEmail: string;
  personName: string;
  role: CustomerAssignmentRecord["role"];
  label: string;
}

/** Who links each key (assignments not retired). */
export function codeHolders(assignments: readonly CustomerAssignmentRecord[]): Map<string, CodeHolder[]> {
  const out = new Map<string, CodeHolder[]>();
  for (const a of assignments.filter(active))
    for (const k of splitKeys(a.customerKeys)) {
      const h = { personEmail: emailKey(a.personEmail), personName: a.personName, role: a.role, label: a.label };
      out.set(k, [...(out.get(k) ?? []), h]);
    }
  return out;
}

/** The other people who link `key` than `row`'s person. */
export const otherHolders = (
  holders: ReadonlyMap<string, readonly CodeHolder[]>,
  key: string,
  row: Pick<CustomerAssignmentRecord, "personEmail">,
): CodeHolder[] => (holders.get(key) ?? []).filter((h) => h.personEmail !== emailKey(row.personEmail));

/** A linked chip's "also Kai (Backup)", "+1" past the first; the full list on hover (`holdersHover`). */
export const alsoLinkedText = (others: readonly CodeHolder[]): string =>
  others.length
    ? `also ${others[0]!.personName} (${others[0]!.role})${others.length > 1 ? ` +${others.length - 1}` : ""}`
    : "";
export const holdersHover = (others: readonly CodeHolder[]): string =>
  others.map((o) => `${o.personName} (${o.role}) · ${o.label}`).join("\n");

/** A suggestion's chip: "linked for Kai" when someone else links it already. */
export const linkedForText = (others: readonly CodeHolder[]): string =>
  others.length ? `linked for ${[...new Set(others.map((o) => o.personName))].join(", ")}` : "";

/** A suggestion's hover: "Not linked. Click to link it to Beall's for Ana". */
export const suggestionHover = (row: Pick<CustomerAssignmentRecord, "label" | "personName">) =>
  `Not linked. Click to link it to ${row.label} for ${row.personName}`;

/** Link all's "Are you sure?" when some suggested codes are linked for others: "3 of these are also linked for other people: BLK (Dijana), …"; null when none is. */
export function sharedBeforeLinkAll(
  suggestions: readonly CustomerOption[],
  holders: ReadonlyMap<string, readonly CodeHolder[]>,
  row: Pick<CustomerAssignmentRecord, "personEmail">,
): string | null {
  const shared = suggestions.flatMap((c) => {
    const others = otherHolders(holders, c.key, row);
    return others.length ? [`${c.code} (${[...new Set(others.map((o) => o.personName))].join(", ")})`] : [];
  });
  if (!shared.length) return null;
  return `${shared.length} of these ${shared.length === 1 ? "is" : "are"} also linked for other people: ${shared.join(", ")}.`;
}

/**
 * Link all: each code linked one after another, as a click on each does (one save and one change entry
 * each), so one that fails leaves the others linked and stays a suggestion.
 */
export async function linkEach(
  keys: readonly string[],
  link: (key: string) => Promise<void>,
  errorText: (e: unknown) => string,
): Promise<{ linked: string[]; failed: { key: string; why: string }[] }> {
  const r = await saveEach(keys, link, errorText);
  return { linked: r.done, failed: r.failed.map((f) => ({ key: f.item, why: f.why })) };
}

/** Saves one after another (no CRUD batch): one that fails leaves the others saved, and says why. */
export async function saveEach<T>(
  items: readonly T[],
  save: (item: T) => Promise<void>,
  errorText: (e: unknown) => string,
): Promise<{ done: T[]; failed: { item: T; why: string }[] }> {
  const done: T[] = [];
  const failed: { item: T; why: string }[] = [];
  for (const item of items) {
    try {
      await save(item);
      done.push(item);
    } catch (e) {
      failed.push({ item, why: errorText(e) });
    }
  }
  return { done, failed };
}

/** Link all's result: "Linked 7 codes to Walmart (Virginia)", "Linked 5 · 2 failed". */
export const linkAllText = (
  r: { linked: readonly string[]; failed: readonly unknown[] },
  row: Pick<CustomerAssignmentRecord, "label" | "personName">,
) =>
  r.failed.length
    ? `Linked ${r.linked.length} · ${r.failed.length} failed`
    : `Linked ${plural(r.linked.length, "code")} to ${row.label} (${row.personName})`;

/**
 * A person's header: "11 customers · 3 not linked" — the labels with no linked code, the rows Needs
 * confirming lists for them; "0 customers" for a person added with none.
 */
export const personSummary = (views: readonly AssignmentView[]) => {
  const open = views.filter(counted);
  const unlinked = open.filter((v) => v.needs).length;
  return `${plural(open.length, "customer")}${unlinked ? ` · ${unlinked} not linked` : ""}`;
};

/** One item of No owner: a customer, or the customers sharing a shown name (Admin → Customers) as one. */
export interface NoOwnerItem {
  id: string;
  /** The shown name of two or more codes; null for a single customer. */
  shown: string | null;
  codes: CustomerOption[];
  lines: number;
}

/**
 * The name a customer shows under, as Routing rules groups them (`customerNamer`): its saved shown name,
 * else its source's own — A2000 its code, Deposco its name — so A2000 MARSHALLS and Deposco "MARSHALLS"
 * are one, as there.
 */
export const shownGroupName = (c: Pick<CustomerOption, "source" | "code" | "name" | "shown">) =>
  c.shown || defaultShown(c);

/** No owner's items: customers sharing a shown name (`shownGroupName`) as one; biggest first (open lines), or by name. */
export function noOwnerItems(customers: readonly CustomerOption[], sort: "lines" | "name" = "lines"): NoOwnerItem[] {
  const byShown = new Map<string, CustomerOption[]>();
  for (const c of customers) {
    const shown = shownGroupName(c);
    byShown.set(shown, [...(byShown.get(shown) ?? []), c]);
  }
  const items: NoOwnerItem[] = [];
  const grouped = new Set<string>();
  for (const [shown, codes] of byShown)
    if (codes.length > 1) {
      codes.forEach((c) => grouped.add(c.key));
      const sorted = [...codes].sort((a, b) => b.openLines - a.openLines || a.code.localeCompare(b.code));
      items.push({ id: `shown:${shown}`, shown, codes: sorted, lines: codes.reduce((n, c) => n + c.openLines, 0) });
    }
  for (const c of customers)
    if (!grouped.has(c.key)) items.push({ id: c.key, shown: null, codes: [c], lines: c.openLines });
  const name = (i: NoOwnerItem) => i.shown ?? (optionName(i.codes[0]!) || i.codes[0]!.code);
  return items.sort(
    (a, b) =>
      (sort === "lines" ? b.lines - a.lines : 0) ||
      (name(a) || a.id).localeCompare(name(b) || b.id) ||
      a.id.localeCompare(b.id),
  );
}

/** The keys of the ticked items: ticking a shown-name group ticks all its codes. */
export const keysOfItems = (items: readonly NoOwnerItem[], ticked: ReadonlySet<string>): string[] =>
  items.filter((i) => ticked.has(i.id)).flatMap((i) => i.codes.map((c) => c.key));

/** An assignment a code is suggested for, waiting to be linked. */
export interface PendingSuggestion {
  id: string;
  personName: string;
  label: string;
  role: CustomerAssignmentRecord["role"];
}

/** Each code's pending suggestions: the assignments (not retired) whose label suggests it. */
export function pendingSuggestions(views: readonly AssignmentView[]): Map<string, PendingSuggestion[]> {
  const out = new Map<string, PendingSuggestion[]>();
  for (const v of views)
    for (const c of v.suggestions) {
      const p = { id: v.row.id, personName: v.row.personName, label: v.row.label, role: v.row.role };
      out.set(c.key, [...(out.get(c.key) ?? []), p]);
    }
  return out;
}

/** An item's pending suggestions, one per assignment, with the item's codes each one suggests. */
export function itemSuggestions(
  item: NoOwnerItem,
  pending: ReadonlyMap<string, readonly PendingSuggestion[]>,
): (PendingSuggestion & { keys: string[] })[] {
  const out = new Map<string, PendingSuggestion & { keys: string[] }>();
  for (const c of item.codes)
    for (const p of pending.get(c.key) ?? []) {
      const had = out.get(p.id) ?? { ...p, keys: [] };
      had.keys.push(c.key);
      out.set(p.id, had);
    }
  return [...out.values()];
}

/** "suggested for Mirihan", "suggested for Mirihan, Kai". */
export const suggestedForText = (s: readonly Pick<PendingSuggestion, "personName">[]) =>
  s.length ? `suggested for ${[...new Set(s.map((p) => p.personName))].join(", ")}` : "";

const LABEL_STOP = new Set(["and", "the", "new", "inc", "llc", "corp", "company", "stores", "store"]);

/**
 * "+ Link"'s list: with nothing typed, the row's suggestions, then the customers whose name or shown
 * name holds a word of the label ("Walmart": WALMART USA, Walmart), then the rest — open lines first,
 * then by name. Typed: the matches, as the search always gave them. `options`: offered, not linked.
 */
export function linkPickerOrder(
  options: readonly CustomerOption[],
  label: string,
  suggestions: readonly CustomerOption[],
  typed = "",
): CustomerOption[] {
  if (typed.trim()) return options.filter((c) => customerMatches(c, typed));
  const first = new Set(suggestions.map((c) => c.key));
  const words = norm(label)
    .split(" ")
    .filter((w) => w.length >= 3 && !LABEL_STOP.has(w));
  const holds = (c: CustomerOption) => words.some((w) => ` ${norm(c.name)} ${norm(c.shown ?? "")} `.includes(` ${w}`));
  const byLines = (a: CustomerOption, b: CustomerOption) =>
    b.openLines - a.openLines || (a.name || a.code).localeCompare(b.name || b.code) || a.key.localeCompare(b.key);
  const suggested = options.filter((c) => first.has(c.key));
  const wordHits = options.filter((c) => !first.has(c.key) && holds(c)).sort(byLines);
  const hit = new Set(wordHits.map((c) => c.key));
  const rest = options.filter((c) => !first.has(c.key) && !hit.has(c.key)).sort(byLines);
  return [...suggested, ...wordHits, ...rest];
}

/** A new assignment as Add, No owner and Assign… save it. */
export type NewAssignment = Pick<
  CustomerAssignmentRecord,
  "personEmail" | "personName" | "label" | "customerKeys" | "role" | "note" | "retired"
>;

/** A customer Add's box offers: a shown name with all its codes, or one customer by its original name or code. */
export interface CustomerPick {
  /** "shown:MARSHALLS", or the customer's key. */
  id: string;
  /** What the assignment's label becomes: the shown name. */
  label: string;
  codes: CustomerOption[];
  openLines: number;
  /** Found by the shown name (every code under it), or by one customer's original name or code. */
  by: "shown" | "customer";
}

/**
 * Add's Customer box, from 2 characters: the shown names holding what is typed (those starting with it
 * first, then most open lines), each with every code under it (`shownGroupName`, as Routing rules);
 * then the customers whose original name or code holds it, not under a shown name listed already.
 * `options`: the customers the Link search offers.
 */
export function customerPicks(options: readonly CustomerOption[], typed: string, max = 12): CustomerPick[] {
  const t = typed.trim().toLowerCase();
  if (t.length < 2) return [];
  const groups = new Map<string, CustomerOption[]>();
  for (const c of options) groups.set(shownGroupName(c), [...(groups.get(shownGroupName(c)) ?? []), c]);
  const starts = (p: CustomerPick) => (p.label.toLowerCase().startsWith(t) ? 0 : 1);
  const order = (a: CustomerPick, b: CustomerPick) =>
    starts(a) - starts(b) || b.openLines - a.openLines || a.label.localeCompare(b.label) || a.id.localeCompare(b.id);
  const shown = [...groups]
    .filter(([name]) => name.toLowerCase().includes(t))
    .map(([name, codes]): CustomerPick => ({
      id: `shown:${name}`,
      label: name,
      codes: [...codes].sort((a, b) => b.openLines - a.openLines || a.key.localeCompare(b.key)),
      openLines: codes.reduce((n, c) => n + c.openLines, 0),
      by: "shown",
    }))
    .sort(order);
  const listed = new Set(shown.flatMap((p) => p.codes.map((c) => c.key)));
  const one = options
    .filter((c) => !listed.has(c.key) && `${c.code} ${c.name}`.toLowerCase().includes(t))
    .map((c): CustomerPick => ({
      id: c.key,
      label: shownGroupName(c),
      codes: [c],
      openLines: c.openLines,
      by: "customer",
    }))
    .sort((a, b) => b.openLines - a.openLines || a.label.localeCompare(b.label) || a.id.localeCompare(b.id));
  return [...shown, ...one].slice(0, max);
}

// Add's Customer list is a multi-select: each suggestion a tick, the list staying open, the ticks kept
// across searches as chips; a code the person has already is not offered.

/** The keys a person links already (active rows): Add's list says "already Shai's" and won't tick them. */
export const keysOfPerson = (assignments: readonly CustomerAssignmentRecord[], email: string): Set<string> =>
  new Set(
    assignments
      .filter((a) => active(a) && emailKey(a.personEmail) === emailKey(email))
      .flatMap((a) => splitKeys(a.customerKeys)),
  );

/** A suggestion as the person may tick it: its codes they don't link yet; `already` when that leaves none. */
export function pickOffer(pick: CustomerPick, mine: ReadonlySet<string>): { pick: CustomerPick; already: boolean } {
  const codes = pick.codes.filter((c) => !mine.has(c.key));
  return {
    pick:
      codes.length === pick.codes.length
        ? pick
        : { ...pick, codes, openLines: codes.reduce((n, c) => n + c.openLines, 0) },
    already: !codes.length,
  };
}

/** The suggestions the list shows: every one, or with "Hide no open lines" those with open lines only. */
export const visiblePicks = (list: readonly CustomerPick[], hideNoOpenLines: boolean) =>
  hideNoOpenLines ? list.filter((p) => p.openLines > 0) : [...list];

/** A row clicked (or Space / Enter): ticked, or unticked when it was; the other ticks, from any search, kept. */
export const togglePick = (ticked: readonly CustomerPick[], pick: CustomerPick): CustomerPick[] =>
  ticked.some((p) => p.id === pick.id) ? ticked.filter((p) => p.id !== pick.id) : [...ticked, pick];

/** "Select all N shown": every suggestion listed that can be ticked, added to the ticks already there. */
export const selectAllShown = (ticked: readonly CustomerPick[], shown: readonly CustomerPick[]): CustomerPick[] => [
  ...ticked,
  ...shown.filter((p) => !ticked.some((t) => t.id === p.id)),
];

/** The customers in the order given, those sharing a shown name as one pick (every code under it), at most `max`. */
function groupPicks(ordered: readonly CustomerOption[], max: number): CustomerPick[] {
  const byName = new Map<string, CustomerOption[]>();
  for (const c of ordered) byName.set(shownGroupName(c), [...(byName.get(shownGroupName(c)) ?? []), c]);
  return [...byName].slice(0, max).map(([name, codes]) => ({
    id: `shown:${name}`,
    label: name,
    codes,
    openLines: codes.reduce((n, c) => n + c.openLines, 0),
    by: "shown" as const,
  }));
}

/**
 * A row's "+ Link" list, the Add list's multi-select: the row's suggestions on top (one code each,
 * marked "suggested", ticked when it opens), then — typed from 2 characters — the customers it may mean
 * (`customerPicks`), else those holding a word of the label, then the rest, open lines first
 * (`linkPickerOrder`), customers sharing a shown name one row. `options`: the customers the Link search offers.
 */
export function linkPicks(
  options: readonly CustomerOption[],
  a: { label: string; suggestions: readonly CustomerOption[]; typed: string },
  max = 50,
): { picks: CustomerPick[]; suggested: CustomerPick[] } {
  const suggested = a.suggestions.map((c): CustomerPick => ({
    id: c.key,
    label: shownGroupName(c),
    codes: [c],
    openLines: c.openLines,
    by: "customer",
  }));
  const taken = new Set(a.suggestions.map((c) => c.key));
  const rest = options.filter((c) => !taken.has(c.key));
  const more =
    a.typed.trim().length >= 2
      ? customerPicks(rest, a.typed, max)
      : groupPicks(linkPickerOrder(rest, a.label, [], a.typed), max);
  return { picks: [...suggested, ...more], suggested };
}

/** The codes of the ticked picks, each once. */
export const codesOfPicks = (picks: readonly CustomerPick[]): CustomerOption[] => [
  ...new Map(picks.flatMap((p) => p.codes).map((c) => [c.key, c])).values(),
];

/** "+ Link"'s button: "Link 4 codes to Belk (Julissa)"; a person with no customer yet "Link 1 code to Iva". */
export const linkTickedText = (codes: number, row: Pick<CustomerAssignmentRecord, "label" | "personName">) =>
  codes
    ? `Link ${plural(codes, "code")} to ${row.label ? `${row.label} (${row.personName})` : row.personName}`
    : "Link";

/** A row's suggested codes ticked: those still suggested, in the suggestions' order (Link N selected). */
export const tickedSuggestions = (suggestions: readonly CustomerOption[], ticked: ReadonlySet<string>) =>
  suggestions.filter((c) => ticked.has(c.key));

/** How several ticked customers are saved: under one label (as the team's sheet), or one assignment each. */
export type AddMode = "one" | "each";

/**
 * What Add saves. Customers ticked: "One assignment" (the default from 2) — one record, the label typed
 * or the first chip's shown name, every ticked code linked; "One per customer" — one each, labelled with
 * its shown name. None ticked: the text typed is a label of its own with no code (its suggestions to
 * confirm); neither, the person alone ("0 customers", `isPersonOnly`) — nothing when they are on the page.
 */
export function addedAssignments(a: {
  email: string;
  name: string;
  role: CustomerAssignmentRecord["role"];
  picks: readonly CustomerPick[];
  typed: string;
  assignments: readonly CustomerAssignmentRecord[];
  mode?: AddMode;
  /** "One assignment"'s label as typed; empty or absent: the first chip's shown name. */
  label?: string | null;
  /** Whether the text typed (nothing ticked) is saved as a label (`addAction` says when); default yes. */
  typedAsLabel?: boolean;
}): NewAssignment[] {
  const base = {
    personEmail: emailKey(a.email),
    personName: a.name.trim(),
    role: a.role,
    note: "",
    retired: "No" as const,
  };
  if (a.picks.length > 1 && (a.mode ?? "one") === "one")
    return [
      {
        ...base,
        label: a.label?.trim() || a.picks[0]!.label,
        customerKeys: joinKeys(a.picks.flatMap((p) => p.codes.map((c) => c.key))),
      },
    ];
  if (a.picks.length)
    return a.picks.map((p) => ({ ...base, label: p.label, customerKeys: joinKeys(p.codes.map((c) => c.key)) }));
  if (a.typed.trim()) return a.typedAsLabel === false ? [] : [{ ...base, label: a.typed.trim(), customerKeys: "" }];
  const onPage = a.assignments.some((x) => active(x) && emailKey(x.personEmail) === base.personEmail);
  return onPage ? [] : [{ ...base, label: "", customerKeys: "" }];
}

/**
 * The Add button says what it does: "Add 5 customers to Shai as Owner" (one assignment), "Add 5
 * assignments to Shai as Owner" (one per customer); a typed label "Add “Kohls” to Shai as Owner"; the
 * person alone "Add Shai"; nothing to add "Add".
 */
export function addButtonText(a: {
  picks: number;
  mode: AddMode;
  person: string;
  role: CustomerAssignmentRecord["role"];
  typed: string;
  saves: number;
}): string {
  const to = a.person ? ` to ${a.person} as ${a.role}` : "";
  if (a.picks > 1) return `Add ${plural(a.picks, a.mode === "one" ? "customer" : "assignment")}${to}`;
  if (a.picks === 1) return `Add 1 customer${to}`;
  if (a.typed.trim()) return `Add “${a.typed.trim()}”${to}`;
  return a.saves && a.person ? `Add ${a.person}` : "Add";
}

/**
 * What Add does with text typed and nothing ticked — a search, not a label, unless said: the list shows
 * matches (or under 2 characters, no list yet), "Tick customers to add", disabled, with the link "Add “TJ”
 * as a label with no codes"; no match, the free label, "Add label “XYZ” (no codes yet) to Shai as Owner".
 * Customers ticked, or nothing typed: `addButtonText`.
 */
export function addAction(a: {
  picks: number;
  mode: AddMode;
  person: string;
  role: CustomerAssignmentRecord["role"];
  typed: string;
  /** The rows the list shows for what is typed. */
  matches: number;
  saves: number;
}): { kind: "add" | "tick" | "label"; text: string; labelLink: string | null } {
  const typed = a.typed.trim();
  if (a.picks || !typed) return { kind: "add", text: addButtonText(a), labelLink: null };
  if (typed.length < 2 || a.matches > 0)
    return { kind: "tick", text: "Tick customers to add", labelLink: `Add “${typed}” as a label with no codes` };
  const to = a.person ? ` to ${a.person} as ${a.role}` : "";
  return { kind: "label", text: `Add label “${typed}” (no codes yet)${to}`, labelLink: null };
}

/**
 * The name the page already shows a person by (their header): their rows' name, the first that isn't a
 * person-only record's; undefined for someone not on the page. Add and Assign… save new rows with it, so
 * adding never renames them (a directory name like "Dijana Icov" for "Dijana (TEST)").
 */
export function pageNameOf(assignments: readonly CustomerAssignmentRecord[], email: string): string | undefined {
  const theirs = assignments.filter((a) => active(a) && emailKey(a.personEmail) === emailKey(email));
  return (theirs.find((a) => !isPersonOnly(a)) ?? theirs[0])?.personName || undefined;
}

/** Add's toast: "Added 5 customers to Shai (Owner)", "Added 5 assignments to Shai (Owner)". */
export const addedText = (a: {
  picks: number;
  mode: AddMode;
  person: string;
  role: CustomerAssignmentRecord["role"];
}) =>
  `Added ${plural(a.picks, a.picks > 1 && a.mode === "each" ? "assignment" : "customer")} to ${a.person} (${a.role})`;

/** The chips left after a save that failed in part: those whose codes were not saved (one per customer: the saved ones go). */
export const picksLeft = (picks: readonly CustomerPick[], failedKeys: readonly string[]): CustomerPick[] => {
  const failed = new Set(failedKeys.flatMap((k) => splitKeys(k)));
  return picks.filter((p) => p.codes.some((c) => failed.has(c.key)));
};

// Assign… (once "Cover"): one person's assignments assigned to another as Backup while the Owner is
// away — the same label and codes, a note saying whose; "Unassign" (once "End cover") retires those
// rows (Show retired brings them back). The records keep their fields; only the words changed.

/** The note an assigned row carries, and how "Unassign" finds them. */
export const coverNote = (name: string) => `Assigned from ${name}`;
/** Rows saved before 11/2026 say "Covering for Ana" (stored text, never rewritten): Unassign reads both. */
const COVER_NOTE = /^(?:Assigned from|Covering for) (.+)$/;

/**
 * One of the absent person's assignments: skipped when the person assigned already has an active row linking
 * all its codes (whatever its label); `sameLabel`, their row with its label that doesn't — assigned
 * anyway, and said; `empty`, no code linked: nothing to assign yet, so not ticked by default.
 */
export interface CoverItem {
  row: CustomerAssignmentRecord;
  keys: string[];
  skip: "codes" | null;
  sameLabel: CustomerAssignmentRecord | null;
  empty: boolean;
}

/** The absent person's active assignments, by label, each with whether the person assigned already has its codes. */
export function coverPlan(assignments: readonly CustomerAssignmentRecord[], from: string, to: string): CoverItem[] {
  const of = (email: string) =>
    assignments.filter((a) => active(a) && !isPersonOnly(a) && emailKey(a.personEmail) === emailKey(email));
  const theirs = of(to).map((a) => ({ a, keys: new Set(splitKeys(a.customerKeys)) }));
  return of(from)
    .sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id))
    .map((row) => {
      const keys = splitKeys(row.customerKeys);
      const skip = keys.length && theirs.some((t) => keys.every((k) => t.keys.has(k))) ? "codes" : null;
      const sameLabel = skip ? null : (theirs.find((t) => norm(t.a.label) === norm(row.label))?.a ?? null);
      return { row, keys, skip, sameLabel, empty: !keys.length };
    });
}

/** Ticked when the dialog opens: not skipped, and something to assign (a code linked). */
export const coverTickedAtFirst = (i: CoverItem) => !i.skip && !i.empty;

/**
 * What an item says beside it: "skipped: already Julissa's (the same codes)", "nothing to assign yet",
 * "Julissa's Belk has no codes; assigned anyway"; "" for none.
 */
export function coverItemNote(i: CoverItem, to: string): string {
  if (i.skip) return `skipped: already ${to}'s (the same codes)`;
  if (i.empty) return "nothing to assign yet";
  if (!i.sameLabel) return "";
  return splitKeys(i.sameLabel.customerKeys).length
    ? `${to}'s ${i.sameLabel.label} doesn't link all these codes; assigned anyway`
    : `${to}'s ${i.sameLabel.label} has no codes; assigned anyway`;
}

/** An assignment assigned to another person: Backup, the same label and codes, the note. */
export const coverInput = (item: CoverItem, to: { email: string; name: string }, note: string): NewAssignment => ({
  personEmail: emailKey(to.email),
  personName: to.name,
  label: item.row.label,
  customerKeys: joinKeys(item.keys),
  role: "Backup",
  note: note.trim(),
  retired: "No",
});

/** "9 of Ana's customers assigned to Julissa as Backup · 2 skipped (already Julissa's) · 1 failed". */
export const coverResultText = (r: { to: string; from: string; done: number; skipped: number; failed: number }) =>
  [
    `${r.done.toLocaleString("en-US")} of ${r.from}'s customers assigned to ${r.to} as Backup`,
    r.skipped ? `${r.skipped.toLocaleString("en-US")} skipped (already ${r.to}'s)` : "",
    r.failed ? `${r.failed.toLocaleString("en-US")} failed` : "",
  ]
    .filter(Boolean)
    .join(" · ");

/** Whose customers a person holds by Assign…: the people named by their active rows' note (either wording), each with those rows. */
export function coversHeld(
  assignments: readonly CustomerAssignmentRecord[],
  email: string,
): { name: string; rows: CustomerAssignmentRecord[] }[] {
  const out = new Map<string, CustomerAssignmentRecord[]>();
  for (const a of assignments) {
    const name = COVER_NOTE.exec(String(a.note ?? "").trim())?.[1];
    if (name && active(a) && emailKey(a.personEmail) === emailKey(email)) out.set(name, [...(out.get(name) ?? []), a]);
  }
  return [...out].map(([name, rows]) => ({ name, rows })).sort((a, b) => a.name.localeCompare(b.name));
}

/** Unassign's "Are you sure?". */
export const endCoverPrompt = (to: string, from: string, n: number) => ({
  title: `Unassign ${from}'s customers from ${to}?`,
  text: `Retires the ${plural(n, "assignment")} ${to} holds as Backup, assigned from ${from}. They stay under Show retired and can be restored.`,
});

/** The System log's note of an Unassign: "Unassigned (assigned from Ana)". */
export const unassignNote = (from: string) => `Unassigned (assigned from ${from})`;

/** The people on the team's sheet (2026-10-28), by their sign-in emails. */
export const SHEET_PEOPLE: readonly { name: string; email: string }[] = [
  { name: "Mirihan", email: "mirihan.goubran@axnygroup.com" },
  { name: "Elma", email: "maria.elma.burleigh@axnygroup.com" },
  { name: "Virginia", email: "virginia.lopez@axnygroup.com" },
  { name: "Ross", email: "rossetti.faamausili@axnygroup.com" },
  { name: "Ana", email: "ana.armendariz@axnygroup.com" },
  { name: "Julissa", email: "julissa.garcia@axnygroup.com" },
  { name: "Kai", email: "kaiulani.santana@axnygroup.com" },
];

/** The sheet as the team sent it: each person's column. Kai's "Belk - Julissa": Julissa owns, Kai backs up. */
export const ASSIGNMENT_SHEET: Readonly<Record<string, readonly string[]>> = {
  Mirihan: [
    "10 Spot",
    "Buffalo Exchange",
    "C&W Shoes",
    "DSW",
    "Forman Mills",
    "Gabriel Brother's BOM",
    "Kohls",
    "Marshalls (NEW)",
    "Shoe Show",
    "Shopper World",
    "TJ Maxx (NEW)",
  ],
  Elma: ["Burlington", "Catco", "Dillards", "JC Penney", "Neiman Marcus / Saks", "TJX Australia"],
  Virginia: ["Amazon", "Costco", "Gabriel Brother's", "Hamrick's", "Harding Brothers", "MCX", "Starboard", "Walmart"],
  Ross: [
    "10 Below",
    "Bucees",
    "Coast Guard",
    "DFA",
    "Factory Connection",
    "Fashion Nova",
    "Mason",
    "Navy Exchange (NEW)",
    "Rack Room",
    "Shopbop",
    "Spencer",
    "TJ UK",
    "United Fashion",
  ],
  Ana: [
    "Beall's",
    "Carnival Cruise",
    "Colony",
    "Hot Topic",
    "Macys Backstage",
    "Sierra Trading",
    "Small International",
    "Versona/Cato/It's Fashion",
    "Winners",
  ],
  Julissa: ["Castle", "Citi Trends", "DDs", "Discovery", "JA transfer", "Nordstrom (NEW)", "Ross", "Shoe Carnival"],
  Kai: [
    "Belk - Julissa",
    "Big Lots - Ana",
    "K&G - Ross",
    "Macys/ Bloomingdale's",
    "Revolve - Elma",
    "Target - Virginia",
    "Tilly's - Ana",
    "Transfers / Quotes - Mirihan",
    "Variety - Mirihan",
    "Zappos - Julissa",
  ],
};

/** The column whose entries name another person after a dash: that person owns, it backs up. */
const BACKUP_COLUMN = "Kai";

/** One row the sheet makes (no keys: a manager links them). */
export type SheetRow = Pick<CustomerAssignmentRecord, "personEmail" | "personName" | "label" | "role" | "note">;

/**
 * The sheet as assignments: each entry its person's, Owner; "(NEW)" or "- NEW" is the note. In
 * Kai's column, "Label - Name" is Name's (Owner) and Kai's (Backup); a label with no name, Kai's
 * (Owner). An unknown name after the dash is not a person: the whole entry is the label.
 */
export function sheetRows(
  sheet: Readonly<Record<string, readonly string[]>> = ASSIGNMENT_SHEET,
  people: readonly { name: string; email: string }[] = SHEET_PEOPLE,
): SheetRow[] {
  const person = (name: string) => people.find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
  const out: SheetRow[] = [];
  for (const [who, entries] of Object.entries(sheet)) {
    const p = person(who);
    if (!p) continue;
    for (const raw of entries) {
      const isNew = /\(new\)|-\s*new\s*$/i.test(raw);
      const text = raw
        .replace(/\(new\)/i, "")
        .replace(/-\s*new\s*$/i, "")
        .trim();
      const note = isNew ? "NEW" : "";
      const dash = text.lastIndexOf(" - ");
      const owner = who === BACKUP_COLUMN && dash > 0 ? person(text.slice(dash + 3)) : undefined;
      const label = owner ? text.slice(0, dash).trim() : text;
      const row = (q: { name: string; email: string }, role: "Owner" | "Backup"): SheetRow => ({
        personEmail: emailKey(q.email),
        personName: q.name,
        label,
        role,
        note,
      });
      if (owner) out.push(row(owner, "Owner"), row(p, "Backup"));
      else out.push(row(p, "Owner"));
    }
  }
  return out;
}

/** `GET /api/routing/my-view`: what opens a page as the person left it, or on their customers. */
export interface MyViewPayload {
  /** The signed-in person's email, lower-case: what their records are by. */
  email: string;
  /** Their customers' keys and labels, and the spellings their workbook rows may have (`mySpellings`). */
  customers: { keys: string[]; labels: string[]; spellings: string[] };
  /** Their saved default of each page, when they saved one. */
  preferences: Partial<Record<"worklist" | "history", UserPreferenceRecord>>;
  /** Every key an Owner has: a manager's "Unassigned customers" chip; null for anyone else. */
  ownedKeys: string[] | null;
  /** Their own `app-user` record: the browser keeps its last seen (`app-user.tsx`); null before the first. */
  appUser: AppUserRecord | null;
}

/** A member of a role group, as Microsoft Graph gives it. */
export interface GraphPerson {
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
}

/** Someone Add may assign: by sign-in email, with how the app knows them. */
export interface PersonOption {
  /** The sign-in email (UPN), lower-case. */
  email: string;
  name: string;
  /** Their mail when it is another address than the sign-in one ("also …"); null when the same. */
  also: string | null;
  /**
   * Their Role, the highest of the groups they are in (`GROUP_MANAGER`, `GROUP_WAREHOUSE`, `GROUP_VIEWER`),
   * one at most; none for someone known otherwise. A Viewer may be an Owner on paper: the tag says they read only.
   */
  roles: ("Manager" | "Warehouse" | "Viewer")[];
  /** When they last opened the routing pages (ISO), null if never seen. */
  lastSeen: string | null;
}

/** `GET /api/routing/admin/people` (the Admin feature). */
export interface PeoplePayload {
  people: PersonOption[];
  /** "not permitted": Graph could not list the role groups (no permission, not set up): the rest only. */
  groups: "ok" | "not permitted";
}

/**
 * The people Add suggests, one per sign-in email, from (in order of trust for the name) the role
 * groups' members, those who signed in, the assignments and the team's sheet. An address that is
 * someone's mail rather than their sign-in name counts as that person. Sorted by name.
 */
export function mergePeople(from: {
  /** Null when Graph could not list them. */
  groups: {
    manager: readonly GraphPerson[];
    warehouse: readonly GraphPerson[];
    viewer?: readonly GraphPerson[];
  } | null;
  signedIn: readonly AppUserRecord[];
  assignments: readonly Pick<CustomerAssignmentRecord, "personEmail" | "personName">[];
  sheet: readonly { name: string; email: string }[];
}): PersonOption[] {
  const people = new Map<string, PersonOption>();
  // mail → sign-in email: someone typed or signed in by their mail is that person.
  const byMail = new Map<string, string>();
  const keyOf = (email: string) => {
    const k = emailKey(email);
    return byMail.get(k) ?? k;
  };
  const add = (email: string, name: string | null | undefined, more: Partial<PersonOption> = {}) => {
    const k = keyOf(email);
    if (!isEmail(k)) return;
    const was = people.get(k);
    people.set(k, {
      email: k,
      name: was?.name || String(name ?? "").trim() || k,
      also: was?.also ?? more.also ?? null,
      roles: [...new Set([...(was?.roles ?? []), ...(more.roles ?? [])])],
      lastSeen:
        [was?.lastSeen, more.lastSeen]
          .filter((t): t is string => !!t)
          .sort()
          .at(-1) ?? null,
    });
  };
  for (const [role, members] of [
    ["Manager", from.groups?.manager ?? []],
    ["Warehouse", from.groups?.warehouse ?? []],
    ["Viewer", from.groups?.viewer ?? []],
  ] as const)
    for (const m of members) {
      const upn = emailKey(m.userPrincipalName);
      const mail = emailKey(m.mail);
      if (!upn) continue;
      const also = mail && mail !== upn ? mail : null;
      if (also) byMail.set(also, upn);
      add(upn, m.displayName, { roles: [role], also });
    }
  for (const u of from.signedIn) add(u.email, u.displayName, { lastSeen: u.lastSeen || null });
  for (const a of from.assignments) add(a.personEmail, a.personName);
  for (const p of from.sheet) add(p.email, p.name);
  // One tag, the highest Role (a Manager is a Warehouse user too). The role groups' members first, then the others.
  const highest = (["Manager", "Warehouse", "Viewer"] as const).slice();
  return [...people.values()]
    .map((p) => ({ ...p, roles: highest.filter((r) => p.roles.includes(r)).slice(0, 1) }))
    .sort(
      (a, b) =>
        Number(!a.roles.length) - Number(!b.roles.length) ||
        a.name.localeCompare(b.name) ||
        a.email.localeCompare(b.email),
    );
}

/** The person an email is: their sign-in email or their other mail, any case; undefined when no one's. */
export const personOfEmail = (people: readonly PersonOption[], email: string): PersonOption | undefined => {
  const k = emailKey(email);
  return k ? people.find((p) => p.email === k || p.also === k) : undefined;
};

/**
 * Add's name after the email changes: a name typed by hand stays; a name the form filled (a pick) follows
 * the email — the known person's name, else empty.
 */
export function nameAfterEmail(
  name: { value: string; byHand: boolean },
  known: Pick<PersonOption, "name"> | undefined,
): { value: string; byHand: boolean } {
  if (name.byHand && name.value.trim()) return name;
  return { value: known?.name ?? "", byHand: false };
}

/** Whether a person matches what was typed: anywhere in their name, sign-in email or other mail. */
export const personMatches = (p: Pick<PersonOption, "name" | "email" | "also">, typed: string): boolean => {
  const words = typed.trim().toLowerCase();
  return !words || [p.name, p.email, p.also ?? ""].join(" ").toLowerCase().includes(words);
};

/** `GET /api/routing/admin/assignments` (the Admin feature): every assignment, retired too, and the customers to link. */
export interface AssignmentsPayload {
  assignments: CustomerAssignmentRecord[];
  customers: CustomerOption[];
}
